<script setup lang="ts">
const businessStore = useBusinessStore();
const { business } = storeToRefs(businessStore);

const { t, locale } = useI18n();

const head = useLocaleHead({
  addDirAttribute: true,
  identifierAttribute: "id",
  addSeoAttributes: true,
});

const defaultTitle = computed(() => {
  if (!business.value) return "bookforme";

  const businessName = business.value.name;
  return locale.value === "en"
    ? `${businessName} - bookforme`
    : `bookforme - ${businessName}`;
});

const { frontendUrl } = useRuntimeConfig().public;
// `useLocaleHead` already resolves <link rel="canonical"> for the current
// route (from i18n's `baseUrl`). Reading it back keeps og:url pinned to the
// same URL instead of a hard-coded string that can drift to a dead domain.
const canonicalUrl = computed(
  () =>
    head.value.link?.find((link) => link.rel === "canonical")?.href ||
    frontendUrl ||
    undefined,
);
// Social platforms need an absolute, raster og:image.
const ogImageUrl = frontendUrl
  ? `${frontendUrl}/og-image.png`
  : "/og-image.png";

// Site-level Organization markup (publisher of every page).
const organizationLd = JSON.stringify(
  Object.fromEntries(
    Object.entries({
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "bookforme",
      url: frontendUrl || undefined,
      logo: ogImageUrl,
    }).filter(([, v]) => v !== undefined),
  ),
).replace(/</g, "\\u003c");

useHead({
  titleTemplate: (titleChunk) => {
    return titleChunk
      ? `${t("meta." + titleChunk)} - ${defaultTitle.value}`
      : defaultTitle.value;
  },
  link: [
    { rel: "icon", href: "/favicon.ico" },
    { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
  ],
  meta: [
    { name: 'viewport', content: 'width=device-width, initial-scale=1, maximum-scale=1' }
  ],
  script: [{ type: "application/ld+json", innerHTML: organizationLd }],
});

useSeoMeta({
  description: t("meta.description"),
  // Open Graph
  ogType: "website",
  ogSiteName: "bookforme",
  ogTitle: t("meta.og_title"),
  ogDescription: t("meta.og_description"),
  ogUrl: () => canonicalUrl.value,
  ogImage: ogImageUrl,
  // Twitter Card
  twitterCard: t("meta.twitter_card") as
    | "summary"
    | "summary_large_image"
    | "app"
    | "player",
  twitterTitle: t("meta.twitter_title"),
  twitterDescription: t("meta.twitter_description"),
  twitterImage: ogImageUrl,
});
</script>

<template>
  <Html :lang="head.htmlAttrs.lang" :dir="head.htmlAttrs.dir">
    <Head>
      <template v-for="link in head.link" :key="link.id">
        <Link
          :id="link.id"
          :rel="link.rel"
          :href="link.href"
          :hreflang="link.hreflang"
        />
      </template>
      <template v-for="meta in head.meta" :key="meta.id">
        <Meta :id="meta.id" :property="meta.property" :content="meta.content" />
      </template>
    </Head>

    <Body>
      <NuxtLoadingIndicator color="#2E83FB" />
      <NuxtLayout>
        <NuxtPage />
      </NuxtLayout>

      <UNotifications />
      <UModals />
      <USlideovers />
    </Body>
  </Html>
</template>

<style>
/* ######### for pages transition ######### */

.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.2s;
}

.slide-left-enter-from {
  opacity: 0;
  transform: translate(50px, 0);
}

.slide-left-leave-to {
  opacity: 0;
  transform: translate(-50px, 0);
}

.slide-right-enter-from {
  opacity: 0;
  transform: translate(-50px, 0);
}

.slide-right-leave-to {
  opacity: 0;
  transform: translate(50px, 0);
}
</style>
